/** @file Mesh buffers of a build: a retained set within 2 MiB, or the build's own set for bigger meshes. */
import { overlayScratchBytes } from '../overlayScratchBytes';
import { edgeTableSlots } from './edgeTableSlots';

/**
 * Buffers for one mesh of V = ceil(uvs.length / 2) vertices: skeleton-space vertices (2V f32), pixels (2V f64; past
 * `pxUsed` all NaN), colours (V u32; past `colorsUsed` all 0), selection marks (V u8) and the edge-key table.
 */
export interface MeshBuffers {
  local: Float32Array;
  px: Float64Array;
  pxUsed: number;
  colors: Uint32Array;
  colorsUsed: number;
  picked: Uint8Array;
  table: Float64Array;
}

/** The retained set never holds more than this (R20-06); a mesh that would push it further gets the build's own set. */
const RETAINED_LIMIT = 2 * 1024 * 1024;

/** An empty buffer set. */
const empty = (): MeshBuffers => ({
  local: new Float32Array(0), px: new Float64Array(0), pxUsed: 0, colors: new Uint32Array(0), colorsUsed: 0, picked: new Uint8Array(0), table: new Float64Array(0),
});

let retained = empty();
/** The largest vertex and triangle-index counts the retained set has been sized for. */
let retainedV = 0, retainedT = 0;
/** This build's own set, for meshes over the budget; dropped by release(). */
let own: MeshBuffers | null = null;

/** Grows every buffer of `b` to what a mesh of v vertices and `slots` table slots needs (exact sizes, never shrinks). */
function fit(b: MeshBuffers, v: number, slots: number): MeshBuffers {
  if (b.local.length < 2 * v) b.local = new Float32Array(2 * v);
  if (b.px.length < 2 * v) {
    b.px = new Float64Array(2 * v).fill(NaN);
    b.pxUsed = 0;
  }
  if (b.colors.length < v) {
    b.colors = new Uint32Array(v);
    b.colorsUsed = 0;
  }
  if (b.picked.length < v) b.picked = new Uint8Array(v);
  if (b.table.length < slots) b.table = new Float64Array(slots);
  return b;
}

/** Bytes of a buffer set sized for v vertices and t triangle indices (fit() allocates exactly this). */
const setBytes = (v: number, t: number): number => overlayScratchBytes(v, t);

/** Scratch buffers per mesh, bounded: buildOverlay calls release() when a build ends. */
export const meshScratch = {
  /**
   * The buffers for a mesh with `uvsLength` uv numbers and `triangleIndexCount` triangle indices: the retained set
   * whenever the mesh alone fits RETAINED_LIMIT (grown for it, or rebuilt for it alone when growing would pass the
   * limit), else this build's own set. The choice depends on the mesh only, so a mesh gets the same set all build.
   */
  forMesh(uvsLength: number, triangleIndexCount: number): MeshBuffers {
    const v = Math.ceil(uvsLength / 2), t = triangleIndexCount;
    if (setBytes(v, t) > RETAINED_LIMIT) {
      own ??= empty();
      return fit(own, v, edgeTableSlots(t));
    }
    if (setBytes(Math.max(retainedV, v), Math.max(retainedT, t)) > RETAINED_LIMIT) {
      retained = empty();
      retainedV = retainedT = 0;
    }
    retainedV = Math.max(retainedV, v);
    retainedT = Math.max(retainedT, t);
    return fit(retained, v, edgeTableSlots(t));
  },
  /** Drops the build's own set (the retained set stays). */
  release(): void {
    own = null;
  },
};
